<script setup>
import {useStore} from "vuex";
import {computed, onMounted, ref, watch} from "vue";
import PrimaryButton from "@/components/buttons/PrimaryButton.vue";
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const store = useStore()

const props = defineProps(['isNew'])

const singleCorrectAnswerId = ref(null)
const temporaryCopy = ref([])

const questionTypes = computed(()=> store.getters['Questions/getQuestionTypes'])
const selectedQuestionType = computed(()=> store.getters['Questions/getQuestion'].type)
const answers = computed(() => store.getters['Answers/getAnswers']);
const displayedAnswers = computed(()=> store.getters['Answers/displayedAnswers'])

const required_rule = value => !!value || t('gamification.required')

onMounted(()=>{
  if(!props.isNew){
    const selected = selectedQuestionType.value
    const trueFalse = questionTypes.value.TrueFalse.value
    const single = questionTypes.value.SingleAnswer.value
    if(selected === trueFalse || selected === single) {
      const correctAnswer = answers.value.find(a => a.correct === true)
      if(correctAnswer){
        singleCorrectAnswerId.value = correctAnswer.id
      }
      if(selected === trueFalse){
        const locAnswers = answers.value.map(a => {
          if(a.text === 'Ano'){
            return {...a, locKey: 'gamification.yes'}
          } else {
            return {...a, locKey: 'gamification.no'}
          }
        })
        store.commit('Answers/setAnswers', locAnswers)
      }
    }
  }
})

watch(() => selectedQuestionType.value, (newType, oldType)=>{
  // If we move away from our own answers (Single/Multiple) towards True/False, we save them, otherwise they will be lost
  if (oldType !== questionTypes.value.TrueFalse.value
      && newType === questionTypes.value.TrueFalse.value) {
    temporaryCopy.value = JSON.parse(JSON.stringify(answers.value))

    const yesNo = JSON.parse(JSON.stringify(store.getters['Answers/getYesNoText']));
    store.commit('Answers/setAnswers', yesNo);

    const correct = yesNo.find(a => a.correct);
    if (correct) singleCorrectAnswerId.value = correct.id;
  }
  // If we go from True/False back to Single or Multiple, we restore the data
  else if (oldType === questionTypes.value.TrueFalse.value &&
      ( newType === questionTypes.value.SingleAnswer.value || newType === questionTypes.value.MultipleAnswers.value)) {

    if (temporaryCopy.value && temporaryCopy.value.length > 0) {
      store.commit('Answers/setAnswers', JSON.parse(JSON.stringify(temporaryCopy.value)));
      if (newType === questionTypes.value.SingleAnswer.value) {
        ensureSingleCorrect();
      }
    }
  }
  // Logic for switching between Single and Multiple
  else if (newType === questionTypes.value.SingleAnswer.value) {
    ensureSingleCorrect();
  }
})

function ensureSingleCorrect(){
  const currentAnswers = JSON.parse(JSON.stringify(answers.value))
  let foundFirst = null;
  currentAnswers.forEach(a => {
    if (a.correct) {
      if (!foundFirst) foundFirst = a;
      else a.correct = false;
    }
  });
  if (!foundFirst && currentAnswers.length > 0) {
    currentAnswers[0].correct = true;
    singleCorrectAnswerId.value = currentAnswers[0].id
  } else {
    singleCorrectAnswerId.value = foundFirst.id
  }
  store.commit('Answers/setAnswers', currentAnswers);
}

function setSingleCorrectAnswer(id){
  singleCorrectAnswerId.value = id
  store.commit('Answers/setSingleCorrectAnswer', id)
}

function addAnswer(){
  store.dispatch('Answers/pushClearAnswer', -1 - answers.value.length)
}

function removeAnswer(id){
  if(props.isNew){
    store.commit('Answers/filterAnswers', id)
  } else {
    store.commit('Answers/setToDelete', id)
  }
}

</script>

<template>
    <div
        v-for="(answer, index) in displayedAnswers"
        :key="answer.id"
        class="pb-3"
    >
      <v-card
          :class="`py-2 px-1 answer-card ${ answer.correct ? 'border-correct' : '_border' }`"
          elevation="0"
      >
        <div class="d-flex align-center justify-space-between ga-3">
          <div class="d-flex justify-center align-center" style="width: 3em">
            <!-- if type of answer is one correct answer or true false answer, radio select is shown -->
            <v-radio-group
                v-if="selectedQuestionType === questionTypes.TrueFalse.value || selectedQuestionType === questionTypes.SingleAnswer.value"
                v-model="singleCorrectAnswerId"
                @update:model-value="setSingleCorrectAnswer"
                hide-details
            >
              <v-radio
                  :value="answer.id"
                  hide-details
                  color="primary"
              />
            </v-radio-group>
            <!-- if type of answer is multiple correct answers, checkbox select is shown -->
            <v-checkbox
                v-else
                v-model="answer.correct"
                class="py-0"
                color="primary"
                variant="underlined"
                hide-details
            />
          </div>
          <v-text-field
              v-if="selectedQuestionType === questionTypes.TrueFalse.value"
              :model-value="$t(answer.locKey)"
              color="#78BE13"
              :label="`${$t('gamification.option')} ${index + 1}`"
              readonly
              variant="underlined"
          ></v-text-field>
          <v-text-field
              v-else
              v-model="answer.text"
              color="#78BE13"
              :label="`${$t('gamification.option')} ${index + 1}`"
              :rules="[required_rule]"
              clearable
              variant="underlined"
          ></v-text-field>

          <div class="d-flex justify-center align-center" style="width: 3.5em">
            <v-btn
                v-if="selectedQuestionType !== questionTypes.TrueFalse.value && displayedAnswers.length > 2"
                icon="delete_outline"
                color="#f34a43"
                variant="text"
                @click="removeAnswer(answer.id)"
            />
          </div>
        </div>
      </v-card>
    </div>
  <PrimaryButton
      v-if="selectedQuestionType !== questionTypes.TrueFalse.value"
      @click="addAnswer"
  >
    <v-icon icon="add" class="mr-md-2" />
    {{$t('gamification.add_new_answer')}}
  </PrimaryButton>
</template>

<style scoped>
.answer-card {
  background-color: #F5F5F5 !important;
  border-radius: 8px;
  width: 100%;
}

.border-correct {
  border: 3px solid #78BE13;
}

._border {
  border: 3px solid white;
}
</style>